import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { HiroMark } from '@/components/brand/HiroMark';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useLanguage, LANGUAGES } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

function scrollToHash(hash: string) {
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Home sections — from /faq or /contact must use /#id, not #id alone. */
function HomeSectionLink({
  hash,
  children,
  className,
  onNavigate,
}: {
  hash: string;
  children: React.ReactNode;
  className?: string;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <Link
      to={{ pathname: '/', hash }}
      className={className ?? 'hover:text-foreground transition-colors'}
      onClick={(e) => {
        if (isHome) {
          e.preventDefault();
          window.history.replaceState(null, '', `/${hash}`);
          scrollToHash(hash);
        }
        onNavigate?.();
      }}
    >
      {children}
    </Link>
  );
}

export function MarketingNav() {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);
  const mobileLinkClass = 'text-base text-foreground py-2 block';

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-md">
      <div
        className="h-1 w-full"
        style={{
          background: 'linear-gradient(90deg, #0f172a 0%, #312e81 50%, #7c3aed 100%)',
        }}
        aria-hidden
      />
      <div className="container flex h-14 items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              window.history.replaceState(null, '', '/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            closeMenu();
          }}
        >
          <HiroMark className="h-8 w-8" />
          <span className="font-display font-bold text-lg tracking-tight">HiroMatch</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground" aria-label="Main">
          <HomeSectionLink hash="#product">{t('marketing.nav.product')}</HomeSectionLink>
          <HomeSectionLink hash="#pricing">{t('marketing.nav.pricing')}</HomeSectionLink>
          <HomeSectionLink hash="#agency">{t('marketing.nav.agency')}</HomeSectionLink>
          <Link to="/faq" className="hover:text-foreground transition-colors">
            {t('marketing.nav.faq')}
          </Link>
          <Link to="/contact" className="hover:text-foreground transition-colors">
            {t('marketing.nav.contact')}
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label={t('marketing.nav.menuOpen')}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[min(100%,20rem)]">
              <SheetHeader>
                <SheetTitle className="font-display text-left">{t('marketing.nav.menuTitle')}</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-6" aria-label="Main mobile">
                <HomeSectionLink hash="#product" className={mobileLinkClass} onNavigate={closeMenu}>
                  {t('marketing.nav.product')}
                </HomeSectionLink>
                <HomeSectionLink hash="#pricing" className={mobileLinkClass} onNavigate={closeMenu}>
                  {t('marketing.nav.pricing')}
                </HomeSectionLink>
                <HomeSectionLink hash="#agency" className={mobileLinkClass} onNavigate={closeMenu}>
                  {t('marketing.nav.agency')}
                </HomeSectionLink>
                <Link to="/faq" className={mobileLinkClass} onClick={closeMenu}>
                  {t('marketing.nav.faq')}
                </Link>
                <Link to="/contact" className={mobileLinkClass} onClick={closeMenu}>
                  {t('marketing.nav.contact')}
                </Link>
                <Link to="/login" className={mobileLinkClass} onClick={closeMenu}>
                  {t('marketing.nav.login')}
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
          <div
            className="hidden sm:flex rounded-md border border-border/80 p-0.5 text-xs"
            role="group"
            aria-label={t('marketing.nav.language')}
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.value}
                type="button"
                className={cn(
                  'px-2 py-1 rounded-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  language === l.value ? 'bg-brand-accent text-white' : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setLanguage(l.value)}
              >
                {l.value.toUpperCase()}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
            <Link to="/login">{t('marketing.nav.login')}</Link>
          </Button>
          <Button size="sm" className="bg-brand-accent hover:bg-brand-accent/90" asChild>
            <Link to="/signup">{t('marketing.nav.trialCta')}</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
